/**
 * Quota-aware localStorage writes.
 *
 * Browsers cap localStorage at ~5 MB per origin. This app was writing two re-fetchable CACHES that
 * between them filled it — a 4 MB MT5 broker catalog (`mt6_brokers_core`, 4,559 servers) and a 2.3 MB
 * news cache (`mt6_vybe_news`) — 6.4 MB total on a live install. Once over the cap EVERY subsequent
 * `setItem` throws `QuotaExceededError`, and every writer here caught it and logged, so the failures
 * were invisible.
 *
 * The damage was not the caches: it was that a 12 KB write of "which account am I on"
 * (`mt6_accounts_active_account`) could no longer land. The terminal therefore rebooted onto whichever
 * account was last written successfully — the client saw it "always connect to FxPro" no matter which
 * account they had selected (SR Global, 2026-09-09).
 *
 * Two tiers fix that ordering problem for good:
 *  - ESSENTIAL (accounts, active account, settings): must never lose to a cache. On a quota error we
 *    evict expendable caches and retry, and we report failure to the caller instead of swallowing it.
 *  - CACHE (brokers, news, calendar): nice-to-have warm-start data. Capped on the way in so it cannot
 *    grow to fill the origin, and dropped without ceremony when space is needed.
 */

/** Re-fetchable caches, in the order they should be sacrificed. Biggest / cheapest to rebuild first. */
export const EXPENDABLE_CACHE_KEYS: readonly string[] = [
  "mt6_vybe_news",
  "mt6_brokers_core",
  "mt6_vybe_economic_calendar",
  "mt6_vybe_economic_calendar_month_versions",
  "mt6_economic_calendar_core",
  "mt6_mailbox_by_account",
];

export function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Chrome/Safari: QuotaExceededError. Firefox: NS_ERROR_DOM_QUOTA_REACHED (code 1014).
  const code = (error as unknown as { code?: number }).code;
  return (
    error.name === "QuotaExceededError" ||
    error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014
  );
}

/** Approximate bytes localStorage is holding (UTF-16: 2 bytes per char). */
export function usageBytes(): number {
  if (typeof window === "undefined") return 0;
  // Standard Storage API (length + key(i)) rather than Object.keys: enumerable own properties are a
  // browser convenience, not part of the interface.
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key === null) continue;
    total += (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2;
  }
  return total;
}

/**
 * Drop expendable caches until `keep` is skipped or they run out. Returns the keys removed.
 * Stops early once `stopWhen` reports success, so we sacrifice as little as possible.
 */
function evictExpendable(keep: string, stopWhen: () => boolean): string[] {
  const dropped: string[] = [];
  for (const key of EXPENDABLE_CACHE_KEYS) {
    if (key === keep) continue;
    if (localStorage.getItem(key) === null) continue;
    try {
      localStorage.removeItem(key);
      dropped.push(key);
    } catch {
      /* removal failing is not recoverable here */
    }
    if (stopWhen()) break;
  }
  return dropped;
}

/**
 * Write something the app cannot afford to lose. On a quota error, expendable caches are evicted and
 * the write retried. Returns false only when the value still could not be stored.
 */
export function writeEssential(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaError(error)) {
      console.error(`[storage] failed to write ${key}:`, error);
      return false;
    }
  }

  let stored = false;
  const dropped = evictExpendable(key, () => {
    try {
      localStorage.setItem(key, value);
      stored = true;
      return true;
    } catch {
      return false;
    }
  });

  if (stored) {
    console.warn(
      `[storage] localStorage was full — dropped ${dropped.join(", ")} to save ${key}. ` +
        "Those caches refill from the network."
    );
    return true;
  }

  console.error(
    `[storage] localStorage is full and ${key} could NOT be saved even after clearing caches ` +
      `(usage ~${Math.round(usageBytes() / 1024)} KB).`
  );
  return false;
}

/**
 * Write warm-start cache data. Never grows past `maxBytes`, and never costs an essential write its
 * space: over the cap (or over quota) the entry is simply not stored.
 */
export function writeCache(key: string, value: string, maxBytes: number): boolean {
  if (typeof window === "undefined") return false;

  if (value.length * 2 > maxBytes) {
    // Too big to be worth an origin's quota — drop any previous copy so it can't linger either.
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return false;
  }

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaError(error)) return false;
    let stored = false;
    evictExpendable(key, () => {
      try {
        localStorage.setItem(key, value);
        stored = true;
        return true;
      } catch {
        return false;
      }
    });
    return stored;
  }
}

/** Cap for the MT5 broker catalog — thousands of servers, re-fetched by `fetchBrokers` on every boot. */
export const BROKERS_CACHE_MAX_BYTES = 1_000_000;
/** Cap for the news cache; the store also trims the article count before calling. */
export const NEWS_CACHE_MAX_BYTES = 500_000;
